import {
  type BookedConsultation as BookedConsultationProto,
  Consultation as ConsultationProto,
} from '@taxfyle/api-internal/internal/consultation_pb'
import {
  ConsultationIncomplete,
  ConsultationIncompleteReason as ConsultationIncompleteReasonProto,
  ConsultationOtherIncompleteReason,
  ConsultationPresetIncompleteReason,
} from '@taxfyle/api-internal/internal/job_pb'
import type {
  ConsultationIncompleteOptions,
  ConsultationProps,
  ConsultationStatus,
} from '@/features/jobs/consultations/Consultation'
import type { TimeRange } from '@/infra/time-range'
import type { PrimeRoomFn } from '@/features/messaging'
import {
  durationToMinutes,
  maybeParseIntervalToTimeRange,
} from '@/utils/grpc-util'
import { isNotNil } from '@/utils/object-util'
import { match } from '@/utils/pattern-match-util'

export function mapConsultationProtoToProps(
  proto: ConsultationProto,
  primeRoom: PrimeRoomFn,
): Partial<ConsultationProps> {
  const requestedLength = proto.getRequestedLength()
  return {
    id: proto.getId(),
    jobId: proto.getJobId(),
    status: mapConsultationStatusProtoToConsultationStatus(proto.getStatus()),
    times: mapAvailabilityProtoToTimeRangeList(proto.getAvailability()),
    clientUserPublicId: proto.getClientUserPublicId(),
    providerUserPublicId: proto.hasProviderUserPublicId()
      ? proto.getProviderUserPublicId()
      : undefined,
    bookedTime: maybeParseIntervalToTimeRange(proto.getBookedTime()) ?? null,
    requestedLength: requestedLength ? durationToMinutes(requestedLength) : 30,
    skipScheduling: proto.getSkipScheduling(),
    room: primeRoom({
      id: proto.hasRoomId() ? proto.getRoomId() : undefined,
    }),
    previouslyBooked: proto.getWasBookedPreviously(),
  }
}

export function mapBookedConsultationProtoToProps(
  proto: BookedConsultationProto,
  primeRoom: PrimeRoomFn,
): Partial<ConsultationProps> {
  return {
    id: proto.getId(),
    jobId: proto.getJobId(),
    status: 'PendingAvailability',
    times: [],
    clientUserPublicId: proto.getClientPublicId(),
    clientDisplayName: proto.getClientDisplayName(),
    providerUserPublicId: proto.getProPublicId(),
    providerDisplayName: proto.getProviderDisplayName(),
    bookedTime: maybeParseIntervalToTimeRange(proto.getBookedTime()) ?? null,
    requestedLength: 30,
    room: primeRoom({ id: undefined }),
    workspaceId: proto.getWorkspaceId(),
  }
}

/**
 * Maps the availability of a consultation to the time ranges it covers.
 */
export function mapAvailabilityProtoToTimeRangeList(
  proto?: ConsultationProto.Availability,
): TimeRange[] {
  return (
    proto
      ?.getAvailabilityTimesList()
      .map(maybeParseIntervalToTimeRange)
      .filter(isNotNil) ?? []
  )
}

function mapConsultationStatusProtoToConsultationStatus(
  status: ConsultationProto.ConsultationStatus,
): ConsultationStatus {
  switch (status) {
    case ConsultationProto.ConsultationStatus.PENDING_AVAILABILITY:
    case ConsultationProto.ConsultationStatus.UNKNOWN:
      return 'PendingAvailability'
    case ConsultationProto.ConsultationStatus.PENDING_BOOKING:
      return 'PendingBooking'
    case ConsultationProto.ConsultationStatus.PENDING_CALL:
      return 'PendingCall'
    case ConsultationProto.ConsultationStatus.IN_PROGRESS:
      return 'InProgress'
    case ConsultationProto.ConsultationStatus.DONE:
      return 'Done'
    case ConsultationProto.ConsultationStatus.CANCELLED:
      return 'Cancelled'
    case ConsultationProto.ConsultationStatus.INCOMPLETE:
      return 'Incomplete'
  }
}

/**
 * Maps why a consultation is closed without being completed.
 *
 * @param options
 */
export function mapConsultationIncompleteOptionsToProto(
  options: ConsultationIncompleteOptions,
): ConsultationIncomplete {
  if (options.reason === 'Other') {
    return new ConsultationIncomplete().setOtherIncompleteReason(
      new ConsultationOtherIncompleteReason().setReason(
        options.otherReason ?? '',
      ),
    )
  }

  return new ConsultationIncomplete().setIncompleteReason(
    new ConsultationPresetIncompleteReason().setReason(
      match(options.reason, {
        ConsultationNotNeeded: () =>
          ConsultationIncompleteReasonProto.CONSULTATION_UNNECESSARY,
        ClientMissedCall: () =>
          ConsultationIncompleteReasonProto.CLIENT_MISSED_CALL,
        ProMissedCall: () => ConsultationIncompleteReasonProto.PRO_MISSED_CALL,
        PreviousProCompletedCall: () =>
          ConsultationIncompleteReasonProto.PREVIOUS_PRO_COMPLETED_CALL,
        ConsultationHandledViaChat: () =>
          ConsultationIncompleteReasonProto.COMPLETED_VIA_CHAT,
      }),
    ),
  )
}
